import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  ButtonComponent, CheckboxComponent, FieldComponent, InputComponent, ModalComponent,
} from '@echospaces-web-monorepo/es-shared-ui';
import type { CompanyMemberRole } from '@echospaces-web-monorepo/es-portal-ecms';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Modal to invite a new member to the organization. Email + optional
 * full name + opt-in roles (`staff` is granted unconditionally on the
 * server, so it's not shown). On success the page reveals the temporary
 * password once if a new Keycloak user was created.
 */
@Component({
  selector: 'es-invite-member-modal',
  imports: [
    FormsModule, ModalComponent, ButtonComponent, FieldComponent,
    InputComponent, CheckboxComponent,
  ],
  template: `
    <es-modal [open]="open" title="Invite member" width="sm" (closed)="onCancel()">
      <div class="p-6 space-y-4">
        <es-field label="Email" [required]="true" [error]="emailError()">
          <es-input
            type="email"
            placeholder="alice@example.com"
            [(ngModel)]="email"
          ></es-input>
        </es-field>

        <es-field label="Full name" hint="Optional — defaults to the email.">
          <es-input placeholder="Alice Adams" [(ngModel)]="fullName"></es-input>
        </es-field>

        <es-field label="Roles" hint="'staff' is granted automatically.">
          <div class="flex flex-col gap-2">
            <es-checkbox [(ngModel)]="roleEditors">Editor</es-checkbox>
            <es-checkbox [(ngModel)]="roleSuperAdmin">Super-Admin</es-checkbox>
          </div>
        </es-field>

        @if (submitError()) {
          <p class="text-sm text-red-400">{{ submitError() }}</p>
        }

        <div class="flex justify-end gap-2 pt-2">
          <es-button variant="ghost" (clicked)="onCancel()">Cancel</es-button>
          <es-button
            variant="primary"
            [disabled]="submitting()"
            (clicked)="onSubmit()"
          >
            {{ submitting() ? 'Inviting…' : 'Invite' }}
          </es-button>
        </div>
      </div>
    </es-modal>
  `,
})
export class InviteMemberModalComponent {
  @Input() open = false;

  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<{
    email: string;
    fullName?: string;
    roles: ReadonlyArray<CompanyMemberRole>;
  }>();

  email = '';
  fullName = '';
  roleEditors = false;
  roleSuperAdmin = false;

  readonly submitting = signal(false);
  readonly emailError = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);

  onSubmit(): void {
    const trimmed = this.email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      this.emailError.set('Enter a valid email address');
      return;
    }
    this.emailError.set(null);

    const roles: CompanyMemberRole[] = [];
    if (this.roleEditors) roles.push('editors');
    if (this.roleSuperAdmin) roles.push('super-admin');

    this.submitting.set(true);
    this.submitted.emit({
      email: trimmed,
      fullName: this.fullName.trim() || undefined,
      roles,
    });
  }

  finishSubmit(error?: string): void {
    this.submitting.set(false);
    this.submitError.set(error ?? null);
    if (!error) {
      this.email = '';
      this.fullName = '';
      this.roleEditors = false;
      this.roleSuperAdmin = false;
      this.closed.emit();
    }
  }

  onCancel(): void {
    this.emailError.set(null);
    this.submitError.set(null);
    this.closed.emit();
  }
}
